import { db, SYNCED_TABLES } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import { scaleNutrients } from '@/engine/portions';
import { ALL_NUTRIENT_KEYS } from '@/engine/nutrients';
import type { MealRow, WorkoutRow } from '@/lib/db';
import { mergeExercises } from '@/features/workout/useWorkout';
import { toCsv } from './csv';

/**
 * Exportación de datos (SPEC 6.E).
 *
 * Todo se genera en el navegador: los datos no pasan por ningún servidor para
 * poder descargarlos, que sería justo lo contrario de lo que se promete.
 */

function download(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type: `${type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function stamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Volcado completo, tabla por tabla, tal cual está en el dispositivo. */
/**
 * Lo que solo vive en el servidor: la caché y el contador de la capa LLM. El
 * dispositivo no lo tiene, pero también es tuyo (RGPD, derecho de acceso).
 */
async function serverOnlyData(): Promise<Record<string, unknown>> {
  if (!supabase) return {};
  const { data } = await supabase.auth.getSession();
  if (!data.session) return {};

  const [cache, usage] = await Promise.all([
    supabase.from('llm_cache').select('kind, response, created_at'),
    supabase.from('llm_usage').select('day, calls'),
  ]);
  if (cache.error || usage.error) {
    return { error: 'No se pudo leer lo guardado en el servidor: vuelve a exportar con conexión.' };
  }
  return { llm_cache: cache.data, llm_usage: usage.data };
}

export async function exportJson(): Promise<void> {
  const data: Record<string, unknown[]> = {};
  for (const table of SYNCED_TABLES) {
    data[table] = await (db[table] as unknown as { toArray(): Promise<unknown[]> }).toArray();
  }

  download(
    `nutrigym-${stamp()}.json`,
    JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        app: 'NutriGym',
        note: 'Volcado completo de tus datos. Un valor null significa desconocido, no cero.',
        data,
        servidor: await serverOnlyData(),
      },
      null,
      2,
    ),
    'application/json',
  );
}

/** Diario de comidas con los nutrientes ya escalados a lo que comiste. */
export async function exportMealsCsv(): Promise<void> {
  const rows = (await db.meals.toArray())
    .filter((m: MealRow) => m.deletedAt === null)
    .sort((a, b) => a.date.localeCompare(b.date));

  const headers = ['fecha', 'franja', 'alimento', 'gramos', ...ALL_NUTRIENT_KEYS];

  download(
    `nutrigym-comidas-${stamp()}.csv`,
    toCsv(
      headers,
      rows.map((row) => {
        const scaled = scaleNutrients(row.per100g, row.grams);
        return [
          row.date,
          row.slot,
          row.foodName,
          row.grams,
          ...ALL_NUTRIENT_KEYS.map((key) => scaled[key] ?? ''),
        ];
      }),
    ),
    'text/csv',
  );
}

/** Una fila por serie: es el formato que sirve para analizar en una hoja. */
export async function exportWorkoutsCsv(): Promise<void> {
  const workouts = (await db.workouts.toArray())
    .filter((w: WorkoutRow) => w.deletedAt === null)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Con el id a secas ("press-banca") la hoja no se lee sola; con los propios
  // ("custom-a1b2…") directamente no se entiende nada.
  const names = new Map(mergeExercises(await db.exercises.toArray()).map((e) => [e.id, e.name]));

  const rows = workouts.flatMap((workout) =>
    workout.sets.map((set) => [
      workout.date,
      set.exerciseId,
      names.get(set.exerciseId) ?? set.exerciseId,
      set.reps,
      set.weightKg,
      set.rir,
      set.warmup,
    ]),
  );

  download(
    `nutrigym-entrenos-${stamp()}.csv`,
    toCsv(
      ['fecha', 'ejercicio_id', 'ejercicio', 'repeticiones', 'peso_kg', 'rir', 'calentamiento'],
      rows,
    ),
    'text/csv',
  );
}

/**
 * Los días cerrados, con los objetivos que estaban vigentes ese día: sin ellos
 * las comidas exportadas no se pueden juzgar contra nada.
 */
export async function exportDaysCsv(): Promise<void> {
  const rows = (await db.days.toArray())
    .filter((d) => d.deletedAt === null)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => [
      d.date,
      d.complete,
      d.targets?.kcal ?? '',
      d.targets?.proteinG ?? '',
      d.targets?.carbsG ?? '',
      d.targets?.fatG ?? '',
    ]);

  download(
    `nutrigym-dias-${stamp()}.csv`,
    toCsv(
      [
        'fecha',
        'completo',
        'objetivo_kcal',
        'objetivo_proteina_g',
        'objetivo_carbos_g',
        'objetivo_grasa_g',
      ],
      rows,
    ),
    'text/csv',
  );
}

/** Alimentos y recetas propios: lo que no está en ningún otro sitio. */
export async function exportFoodsCsv(): Promise<void> {
  const rows = (await db.foods.toArray())
    .filter((f) => f.deletedAt === null && f.kind !== 'generico')
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    .map((food) => [
      food.name,
      food.kind,
      food.barcode ?? '',
      food.brand ?? '',
      food.packageGrams ?? '',
      food.servingGrams ?? '',
      ...ALL_NUTRIENT_KEYS.map((key) => food.per100g[key] ?? ''),
    ]);

  download(
    `nutrigym-alimentos-${stamp()}.csv`,
    toCsv(
      ['nombre', 'tipo', 'codigo_barras', 'marca', 'envase_g', 'porcion_g', ...ALL_NUTRIENT_KEYS],
      rows,
    ),
    'text/csv',
  );
}

export async function exportWeightsCsv(): Promise<void> {
  const rows = (await db.bodyWeights.toArray())
    .filter((w) => w.deletedAt === null)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((w) => [w.date, w.kg]);

  download(`nutrigym-peso-${stamp()}.csv`, toCsv(['fecha', 'peso_kg'], rows), 'text/csv');
}
